import { Agent } from '../types';
import { AGENTS_URL } from '../config/api-config';
import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';

interface APIError {
  message: string;
  code?: string;
  details?: any;
  status?: number;
}

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

export const useAgentsApi = () => {

  const { getAuthToken } = useAuth();
  const getHeaders = createApiHeadersGetter(getAuthToken);

  const getAgents = async (getAllAgents: boolean = false): Promise<Agent[]> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(
        `${AGENTS_URL}?getAllAgents=${getAllAgents}`,
        {
          method: 'GET',
          headers
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch Agents');
      }
      return response.json();
    } catch (error) {
      console.error('Error fetching Agents:', error);
      throw error;
    }
  };

  const getAgent = async (id: string): Promise<Agent> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${AGENTS_URL}/${id}`,
        {
          method: 'GET',
          headers
        }
      );
      console.log('[getAgent] Response:', response);

      if (!response.ok) {
        let errorMessage = 'Failed to fetch Agent';
        let errorDetails;
        
        try {
          // Try to parse error response as JSON
          const errorData: APIError = await response.json();
          errorMessage = errorData.message || errorMessage;
          errorDetails = errorData.details;
        } catch (parseError) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new ApiError(errorMessage, response.status, errorDetails);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching Agent:', error);
      // If it's already an ApiError, rethrow it
      if (error instanceof ApiError) {
        throw error;
      }
      // Otherwise wrap it in an ApiError
      throw new ApiError(error.message || 'Unknown error occurred', 500);
    }
  };

  const createAgent = async (agent: Omit<Agent, 'id'>): Promise<Agent> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${AGENTS_URL}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(agent)
        }
      );

      if (!response.ok) {
        console.log('[createAgent] Error creating Agent response:', response);
        try {
          // Try to parse error response as JSON
          const errorData: APIError = await response.json();
          console.log('[createAgent] Error creating Agent errorData:', errorData);
          throw new Error(errorData.message || 'Failed to create Agent');
        } catch (parseError) {
          console.log('[createAgent] Error parsing JSON:', String(parseError));
          console.log('[createAgent] Error parsing JSON Type:', typeof (parseError));
          throw new Error(String(parseError));
        }
      }
      return response.json();
    } catch (error) {
      console.error('Error creating Agent:', error);
      throw error;
    }
  };

  const updateAgent = async (agent: Partial<Agent>): Promise<Agent> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${AGENTS_URL}/${agent.agentId}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(agent)
        }
      );
      if (!response.ok) {
        throw new Error('Failed to update Agent');
      }
      return response.json();
    } catch (error) {
      console.error('Error updating Agent:', error);
      throw error;
    }
  };

  const deleteAgent = async (id: string): Promise<void> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${AGENTS_URL}/${id}`,
        {
          method: 'DELETE',
          headers
        }
      );
      if (!response.ok) {
        throw new Error('Failed to delete Agent');
      }
      return;
    } catch (error) {
      console.error('Error deleting Agent:', error);
      throw error;
    }
  };

  const checkAgentNameExists = async (name: string, excludeId?: string): Promise<boolean> => {
    try {
      const getAllAgents = true;
      const allAgents = await getAgents(getAllAgents);
      return allAgents.some(agent =>
        agent.agentName.toLowerCase() === name.toLowerCase() &&
        agent.agentId !== excludeId
      );
    } catch (error) {
      console.error('Error checking agent name:', error);
      throw error;
    }
  };

  return {
    getAgents,
    getAgent,
    createAgent,
    updateAgent,
    deleteAgent,
    checkAgentNameExists
  };
}